import React, { createContext, useContext, useEffect, useState } from 'react';
import { ComponentRegistry } from '../services/ComponentRegistry';
import { useISRContext } from '../../Hooks/useISRContext';

type ComponentDefinition = {
    id: string;
    name: string;
    type: 'submodel' | 'field' | 'category' | 'connector' | 'widget';
    isReusable: boolean;
    parentId?: string;
    widgetType?: string;
    fieldType?: string;
    dataConfiguration?: {
      choices?: {
        av_id: string;
        av_value: string;
        display_value: string;
      }[];
      [key: string]: any;
    };
  }

interface ComponentWithUsageInfo extends ComponentDefinition {
  usageStatus: {
    isUsed: boolean;
    isDisabled: boolean;
  };
}

type ComponentRegistryContextType = {
  registry: ComponentRegistry | null;
  isLoading: boolean;
  error: string | null;
  registryVersion: number;
  resetRegistry: () => void;
  
  canUseComponent: (componentId: string, containerId: string, currentPageId?: string) => boolean;
  trackComponentUsage: (componentId: string, location: { containerId: string; pageId: string; groupId: string }) => void;
  releaseComponent: (componentId: string, containerId: string) => void;
  getAvailableComponents: (containerId: string, currentPageId?: string) => ComponentDefinition[];
  getUsageMap: () => any;
  
  getAllComponentsWithUsageInfo: (containerId: string, currentPageId?: string) => ComponentWithUsageInfo[];
  getContainerSubmodelId: (containerId: string) => string | undefined;
  getComponentDefinition: (componentId: string) => ComponentDefinition | undefined;

  setContainerParent: (childId: string, parentId: string) => void;
  removeContainerFromHierarchy: (containerId: string) => void;
  getRootContainerId: (containerId: string) => string;
  
  moveWidget: (widgetId: string, fromContainerId: string, toContainerId: string, location: { containerId: string; pageId: string; groupId: string }) => void;
  saveWidgetConfig: (widgetId: string, config: any) => void;
  getWidgetConfig: (widgetId: string) => any;
  clearWidgetConfig: (widgetId: string) => void;
  isWidgetInContainer: (widgetId: string, containerId: string) => boolean;
  getWidgetContainer: (widgetId: string) => string | undefined;
  
  
  canAddYourBuildToContainer: (containerId: string, pageId: string) => boolean;
  getYourBuildContainerOnPage: (pageId: string) => string | null;
  isYourBuildOnPage: (pageId: string) => boolean;
  getYourBuildWidgetIdOnPage: (pageId: string) => string | null;
}

const ComponentRegistryContext = createContext<ComponentRegistryContextType>({
  registry: null,
  isLoading: true,
  error: null,
  registryVersion: 1,
  canUseComponent: () => false,
  trackComponentUsage: () => {},
  releaseComponent: () => {},
  getAvailableComponents: () => [],
  getUsageMap: () => ({}),
  getAllComponentsWithUsageInfo: () => [],
  getContainerSubmodelId: () => undefined,
  getComponentDefinition: () => undefined,
  setContainerParent: () => {},
  removeContainerFromHierarchy: () => {},
  getRootContainerId: () => '',
  resetRegistry: () => {},
  moveWidget: () => {},
  saveWidgetConfig: () => {},
  getWidgetConfig: () => null,
  clearWidgetConfig: () => {},
  isWidgetInContainer: () => false,
  getWidgetContainer: () => undefined,
  canAddYourBuildToContainer: () => false,
  getYourBuildContainerOnPage: () => null,
  isYourBuildOnPage: () => false,
  getYourBuildWidgetIdOnPage: () => null,
});

export const ComponentRegistryProvider: React.FC<{children: React.ReactNode;}> = ({ children }) => {
  const isrContext = useISRContext();
  const [registry, setRegistry] = useState<ComponentRegistry | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [registryVersion, setRegistryVersion] = useState(0);

    useEffect(() => {
      if (isrContext?.networkUserProjectDataModels) {
        try {
          const registryInstance = new ComponentRegistry(isrContext?.networkUserProjectDataModels);
          setRegistry(registryInstance);
          setIsLoading(false);
      } catch (err) {
        console.error('Failed to initialize component registry:', err);
        setError('Failed to initialize component registry');
        setIsLoading(false);
      }
    }
  }, [isrContext?.networkUserProjectDataModels]);

  const canUseComponent = (componentId: string, containerId: string, currentPageId?: string): boolean => {
    if (!registry) return false;
    return registry.canUseComponent(componentId, containerId, currentPageId);
  };

  const trackComponentUsage = ( componentId: string, location: { containerId: string; pageId: string; groupId: string }
  ): void => {
    if (!registry) return;
    registry.trackComponentUsage(componentId, location);
    setRegistryVersion(prev => prev + 1);
  };

  const releaseComponent = (componentId: string, containerId: string): void => {
    if (!registry) return;
    registry.releaseComponent(componentId, containerId);
    setRegistryVersion(prev => prev + 1);
  };

  const setContainerParent = (childId: string, parentId: string): void => {
    if (!registry) return;
    console.log(`Setting parent of ${childId} to ${parentId}`);
    registry.setContainerParent(childId, parentId);
  };

  const removeContainerFromHierarchy = (containerId: string): void => {
    if (!registry) return;
    registry.removeContainerFromHierarchy(containerId);
  };

  const getRootContainerId = (containerId: string): string => {
    if (!registry) return containerId;
    console.log(`Getting root container ID for ${containerId}`);
    return registry.getRootContainerId(containerId);
  };


  const getAvailableComponents = (containerId: string, currentPageId?: string): ComponentDefinition[] => {
    if (!registry) return [];
    return registry.getAvailableComponents(containerId, currentPageId);
  };

  const getUsageMap = (): any => {
    if (!registry) return {};
    return registry.getUsageMap();
  };
  
  const getAllComponentsWithUsageInfo = (containerId: string, currentPageId?: string): ComponentWithUsageInfo[] => {
    if (!registry) return [];
    return registry.getAllComponentsWithUsageInfo(containerId, currentPageId);
  };
  
  const getContainerSubmodelId = (containerId: string): string | undefined => {
    if (!registry) return undefined;
    return registry.getContainerSubmodelId(containerId);
  };
  
  const getComponentDefinition = (componentId: string): ComponentDefinition | undefined => {
    if (!registry) return undefined;
    return registry.getComponentDefinition(componentId);
  };

  const resetRegistry = (): void => {
    if (!registry) return;
    registry.resetRegistry();
    setRegistryVersion(prev => prev + 1);
  };

  const moveWidget = (widgetId: string, fromContainerId: string, toContainerId: string, location: { containerId: string; pageId: string; groupId: string }): void => {
    if (!registry) return;
    registry.moveWidget(widgetId, fromContainerId, toContainerId, location);
    setRegistryVersion(prev => prev + 1);
  };

  const saveWidgetConfig = (widgetId: string, config: any): void => {
    if (!registry) return;
    registry.saveWidgetConfig(widgetId, config);
  };

  const getWidgetConfig = (widgetId: string): any => {
    if (!registry) return null;
    return registry.getWidgetConfig(widgetId);
  };

  const clearWidgetConfig = (widgetId: string): void => {
    if (!registry) return;
    registry.clearWidgetConfig(widgetId);
  };

  const isWidgetInContainer = (widgetId: string, containerId: string): boolean => {
    if (!registry) return false;
    return registry.isWidgetInContainer(widgetId, containerId);
  };

  const getWidgetContainer = (widgetId: string): string | undefined => {
    if (!registry) return undefined;
    return registry.getWidgetContainer(widgetId);
  };

  const canAddYourBuildToContainer = (containerId: string, pageId: string): boolean => {
    if (!registry) return false;
    return registry.canAddYourBuildToContainer(containerId, pageId);
  };

  const getYourBuildContainerOnPage = (pageId: string): string | null => {
    if (!registry) return null;
    return registry.getYourBuildContainerOnPage(pageId);
  };

  const isYourBuildOnPage = (pageId: string): boolean => {
    if (!registry) return false;
    return registry.isYourBuildOnPage(pageId);
  };

  const getYourBuildWidgetIdOnPage = (pageId: string): string | null => {
    if (!registry) return null;
    return registry.getYourBuildWidgetIdOnPage(pageId);
  };

  const contextValue: ComponentRegistryContextType = {
    registry,
    isLoading,
    error,
    canUseComponent,
    trackComponentUsage,
    releaseComponent,
    getAvailableComponents,
    getUsageMap,
    getAllComponentsWithUsageInfo,
    getContainerSubmodelId,
    getComponentDefinition,
    setContainerParent,
    removeContainerFromHierarchy,
    getRootContainerId,
    registryVersion,
    resetRegistry,
    moveWidget,
    saveWidgetConfig,
    getWidgetConfig,
    clearWidgetConfig,
    isWidgetInContainer,
    getWidgetContainer,
    canAddYourBuildToContainer,
    getYourBuildContainerOnPage,
    isYourBuildOnPage,
    getYourBuildWidgetIdOnPage,
  };

  return (
    <ComponentRegistryContext.Provider value={contextValue}>
      {children}
    </ComponentRegistryContext.Provider>
  );
};

export const useComponentRegistry = () => {
  const context = useContext(ComponentRegistryContext);
  if (!context) {
    throw new Error('useComponentRegistry must be used within a ComponentRegistryProvider');
  }
  return context;
};